import { useMemo } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

/**
 * MoodChart — last 14 days of mood intensity, color-coded by dominant mood per day.
 * `moods` = [{ mood, intensity, created_at }]
 */
const MOOD_COLORS = {
    calm: "#8A9A86",
    joyful: "#9EC3E3",
    tired: "#7A736C",
    anxious: "#4F86C6",
    low: "#6B7A87",
    angry: "#D95D5D",
};

function dayKey(iso) {
    const d = new Date(iso);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
}

export default function MoodChart({ moods = [] }) {
    const data = useMemo(() => {
        // Last 14 days, oldest -> newest
        const days = [];
        const today = new Date(); today.setHours(0, 0, 0, 0);
        for (let i = 13; i >= 0; i--) {
            const d = new Date(today); d.setDate(today.getDate() - i);
            days.push({ key: d.toISOString().slice(0, 10), label: d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" }) });
        }
        const grouped = {};
        moods.forEach(m => {
            const k = dayKey(m.created_at);
            grouped[k] = grouped[k] || [];
            grouped[k].push(m);
        });
        return days.map(d => {
            const items = grouped[d.key] || [];
            // Average intensity, dominant mood
            let avg = null;
            let dominant = null;
            if (items.length) {
                avg = items.reduce((s, x) => s + (x.intensity || 0), 0) / items.length;
                const counts = {};
                items.forEach(x => { counts[x.mood] = (counts[x.mood] || 0) + 1; });
                dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
            }
            return { day: d.label, intensity: avg, mood: dominant };
        });
    }, [moods]);

    const hasData = data.some(d => d.intensity != null);

    if (!hasData) {
        return (
            <div className="text-inkSoft font-body italic text-sm" data-testid="mood-chart-empty">
                start logging moods and a 14-day rhythm will appear here.
            </div>
        );
    }

    return (
        <div data-testid="mood-chart" className="w-full h-52">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id="moodFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#4F86C6" stopOpacity={0.55} />
                            <stop offset="100%" stopColor="#4F86C6" stopOpacity={0.05} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E5DFD3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: "#7A736C", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 5]} ticks={[1, 3, 5]} tick={{ fill: "#7A736C", fontSize: 11, fontFamily: "Manrope" }} axisLine={false} tickLine={false} />
                    <Tooltip
                        contentStyle={{ background: "#F7F5F0", border: "1px solid #E5DFD3", borderRadius: 12, fontFamily: "Manrope", fontSize: 13 }}
                        formatter={(value, _, item) => {
                            const mood = item?.payload?.mood;
                            return [`${value?.toFixed(1) ?? "—"} (${mood ?? "—"})`, "intensity"];
                        }}
                    />
                    <Area type="monotone" dataKey="intensity" stroke="#4F86C6" strokeWidth={2.5} fill="url(#moodFill)" connectNulls />
                </AreaChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-inkSoft font-body">
                {Object.entries(MOOD_COLORS).map(([k, c]) => (
                    <span key={k} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: c }} />{k}
                    </span>
                ))}
            </div>
        </div>
    );
}
